/**
 * CLIENT-SIDE SCORING CALCULATOR
 *
 * This module provides HEURISTIC-ONLY score estimates using neutral weights.
 * It is NOT the authoritative scoring engine — that lives server-side in the
 * `ai-score` / `score-startup` edge functions (`runScoring()` + the v2/v2.1
 * methodology bundle), producing immutable `score_event` rows.
 *
 * Use cases:
 * - Offline/mock mode score preview
 * - Quick client-side estimate before server score is available
 * - Founder self-assessment during onboarding
 *
 * The server-side engine uses proprietary weights and methodology stored
 * in the `scoring_config` database table. Client estimates may differ
 * significantly from server scores.
 *
 * This calculator uses neutral/equal weights from methodology.ts stubs.
 * Do NOT add proprietary scoring logic here — keep it server-side only.
 */

import type { StartupProfile } from '@/lib/state/profile-store';
import type { ScoreCriteria, SubCriterionScore } from './types';
import type { FundingStage } from '@/lib/dataroom/types';
import {
  type ScoringDimension,
  type DimensionScore,
  type StartupScore,
  type EvidenceLevel,
  type RiskLevel,
  DIMENSION_DEFINITIONS,
  DIMENSION_ORDER,
  STAGE_WEIGHTS,
  CONFIDENCE_MULTIPLIERS,
  calculateRiskLevel,
  getInvestmentReadinessLevel,
  getDimensionDefinition,
} from './methodology';
import { SCORING_RULES, evaluateSubCriterion, getRulesForDimension, type SubCriterionResult } from './rules';

// =============================================================================
// TYPES
// =============================================================================

export interface CalculatorOptions {
  /** Override stage (defaults to profile.stage) */
  stage?: FundingStage;
  /** Include detailed sub-scores */
  includeSubScores?: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Convert profile stage string to FundingStage type
 * Handles various stage formats
 */
function normalizeStagee(stage: string | undefined): FundingStage {
  if (!stage) return 'seed';

  const normalized = stage.toLowerCase().replace(/_/g, '-');

  // Map various formats to FundingStage
  const stageMap: Record<string, FundingStage> = {
    'pre-seed': 'pre-seed',
    'preseed': 'pre-seed',
    'seed': 'seed',
    'series-a': 'series-a',
    'series_a': 'series-a',
    'seriesa': 'series-a',
    'series-b': 'series-b-plus',
    'series_b': 'series-b-plus',
    'series-b-plus': 'series-b-plus',
    'series-c': 'series-b-plus',
    'pre-ipo': 'pre-ipo',
    'preipo': 'pre-ipo',
  };

  return stageMap[normalized] ?? 'seed';
}

/**
 * Get the lowest (worst) evidence level from a list
 */
function getLowestEvidenceLevel(levels: EvidenceLevel[]): EvidenceLevel {
  const priority: EvidenceLevel[] = ['missing', 'self-declared', 'in-progress', 'uploaded', 'verified'];

  let lowest: EvidenceLevel = 'verified';
  for (const level of levels) {
    if (priority.indexOf(level) < priority.indexOf(lowest)) {
      lowest = level;
    }
  }

  return lowest;
}

/**
 * Calculate weighted average score from sub-criteria
 */
function calculateWeightedScore(subResults: SubCriterionResult[], dimension: ScoringDimension): number {
  const definition = getDimensionDefinition(dimension);
  if (!definition) {
    // Simple average if no definition found
    const sum = subResults.reduce((acc, r) => acc + r.score, 0);
    return subResults.length > 0 ? (sum / subResults.length) * 10 : 50;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  for (const result of subResults) {
    const subDef = definition.subCriteria.find((sc) => sc.id === result.criterionId);
    const weight = subDef?.weight ?? 1 / subResults.length;

    weightedSum += result.score * weight;
    totalWeight += weight;
  }

  // Convert 0-10 sub-scores to 0-100 dimension score
  return totalWeight > 0 ? (weightedSum / totalWeight) * 10 : 50;
}

/**
 * Generate feedback summary for a dimension
 */
function generateDimensionFeedback(
  dimension: ScoringDimension,
  subResults: SubCriterionResult[],
  score: number
): string {
  // Find the lowest scoring sub-criterion for actionable feedback
  const lowestResult = subResults.reduce(
    (lowest, current) => (current.score < lowest.score ? current : lowest),
    subResults[0]
  );

  const definition = getDimensionDefinition(dimension);
  const dimensionName = definition?.name ?? dimension;

  if (score >= 80) {
    return `Strong ${dimensionName.toLowerCase()} foundation. ${lowestResult?.feedback ?? ''}`;
  } else if (score >= 60) {
    return `Good ${dimensionName.toLowerCase()} with room for improvement. ${lowestResult?.feedback ?? ''}`;
  } else if (score >= 40) {
    return `${dimensionName} needs attention. ${lowestResult?.feedback ?? ''}`;
  } else {
    return `Critical ${dimensionName.toLowerCase()} gaps. ${lowestResult?.feedback ?? ''}`;
  }
}

// =============================================================================
// MAIN CALCULATOR FUNCTIONS
// =============================================================================

/**
 * Calculate score for a single dimension
 */
export function calculateDimensionScore(
  dimension: ScoringDimension,
  profile: StartupProfile,
  stage: FundingStage
): DimensionScore {
  // Get rules for this dimension
  const rules = getRulesForDimension(dimension);

  // Evaluate each sub-criterion
  const subResults: SubCriterionResult[] = rules.map((rule) => evaluateSubCriterion(rule, profile));

  // Calculate weighted dimension score
  const rawScore = Math.round(calculateWeightedScore(subResults, dimension));

  // Get dimension weight for this stage
  const weight = STAGE_WEIGHTS[stage]?.[dimension] ?? 0.08;
  const weightedScore = rawScore * weight;

  // Determine evidence level (lowest of sub-criteria)
  const evidenceLevels = subResults.map((r) => r.evidence);
  const evidenceLevel = getLowestEvidenceLevel(evidenceLevels);

  // Calculate confidence-adjusted score
  const confidenceMultiplier = CONFIDENCE_MULTIPLIERS[evidenceLevel];
  const confidenceAdjustedScore = weightedScore * confidenceMultiplier;

  // Calculate risk level
  const riskLevel = calculateRiskLevel(rawScore);

  // Generate feedback
  const feedback = generateDimensionFeedback(dimension, subResults, rawScore);

  // Build sub-scores array
  const subScores = subResults.map((r) => ({
    criterionId: r.criterionId,
    score: r.score,
    maxScore: r.maxScore,
    feedback: r.feedback,
  }));

  return {
    dimension,
    rawScore,
    weightedScore,
    evidenceLevel,
    confidenceAdjustedScore,
    riskLevel,
    feedback,
    subScores,
  };
}

/**
 * Calculate complete startup score across all dimensions
 */
export function calculateStartupScore(
  profile: StartupProfile,
  options: CalculatorOptions = {}
): StartupScore {
  // Normalize stage
  const stage = options.stage ?? normalizeStagee(profile.stage);

  // Calculate all dimension scores
  const dimensionScores: DimensionScore[] = DIMENSION_ORDER.map((dimension) =>
    calculateDimensionScore(dimension, profile, stage)
  );

  // Calculate overall score (weighted sum)
  let overallScore = 0;
  for (const ds of dimensionScores) {
    overallScore += ds.weightedScore;
  }
  overallScore = Math.round(overallScore);

  // Calculate overall confidence multiplier (weighted average)
  let weightedConfidence = 0;
  let totalWeight = 0;
  for (const ds of dimensionScores) {
    const weight = STAGE_WEIGHTS[stage]?.[ds.dimension] ?? 0.08;
    weightedConfidence += CONFIDENCE_MULTIPLIERS[ds.evidenceLevel] * weight;
    totalWeight += weight;
  }
  const confidenceMultiplier = totalWeight > 0 ? Math.round((weightedConfidence / totalWeight) * 100) / 100 : 0.7;

  // Calculate adjusted score
  const adjustedScore = Math.round(overallScore * confidenceMultiplier);

  // Count risks
  const riskSummary = {
    high: dimensionScores.filter((ds) => ds.riskLevel === 'high').length,
    medium: dimensionScores.filter((ds) => ds.riskLevel === 'medium').length,
    low: dimensionScores.filter((ds) => ds.riskLevel === 'low').length,
  };

  // Determine investment readiness level.
  // B29: band the RAW overallScore, matching the authoritative server banding (which
  // bands the raw finalScore). Banding the confidence-discounted adjustedScore made
  // the onboarding preview show a readiness level up to one band below the real one.
  // adjustedScore remains available as a separate displayed number.
  const investmentReadinessLevel = getInvestmentReadinessLevel(overallScore);

  return {
    overallScore,
    confidenceMultiplier,
    adjustedScore,
    stage,
    dimensionScores,
    riskSummary,
    investmentReadinessLevel,
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Convert StartupScore to ScoreCriteria[] format used by inverio-store
 */
export function toScoreCriteria(startupScore: StartupScore): ScoreCriteria[] {
  const criteria: ScoreCriteria[] = [];

  for (const ds of startupScore.dimensionScores) {
    const definition = getDimensionDefinition(ds.dimension);

    // Convert sub-scores to SubCriterionScore format
    const subScores: SubCriterionScore[] = ds.subScores.map((sub) => ({
      id: sub.criterionId,
      name: definition?.subCriteria.find((sc) => sc.id === sub.criterionId)?.name ?? sub.criterionId,
      score: sub.score,
      maxScore: sub.maxScore,
      feedback: sub.feedback,
    }));

    // Create ScoreCriteria entry
    const criterion: ScoreCriteria = {
      id: `dim-${ds.dimension}-calculated`,
      name: definition?.name ?? ds.dimension,
      dimension: ds.dimension as ScoreCriteria['dimension'],
      score: ds.rawScore,
      maxScore: 100,
      feedback: ds.feedback,
      recommendation: generateQuickRecommendation(ds),
      riskLevel: ds.riskLevel,
      evidenceLevel: ds.evidenceLevel,
      verified: ds.evidenceLevel === 'verified',
      verificationNote: getVerificationNote(ds.evidenceLevel),
      subScores,
    };

    criteria.push(criterion);
  }

  return criteria;
}

/**
 * Generate a quick recommendation for a dimension
 */
function generateQuickRecommendation(ds: DimensionScore): string {
  // Find lowest sub-criterion for targeted recommendation
  const lowestSub = ds.subScores.reduce(
    (lowest, current) => (current.score < lowest.score ? current : lowest),
    ds.subScores[0]
  );

  if (!lowestSub) {
    return 'Upload supporting documents to improve this dimension score.';
  }

  // Generate contextual recommendation based on dimension and sub-criterion
  const recommendations: Record<string, Record<string, string>> = {
    team: {
      'team-founder-experience': 'Highlight previous startup or relevant industry experience in your profile.',
      'team-completeness': 'Consider hiring key roles or adding advisors to strengthen the team.',
      'team-domain-expertise': 'Articulate your unique insights and experience with the problem space.',
      'team-coachability': 'Document your track record of pivoting based on feedback.',
    },
    product: {
      'product-stage': 'Focus on acquiring more paying customers to demonstrate product maturity.',
      'product-quality': 'Collect and showcase user testimonials and NPS scores.',
      'product-differentiation': 'Document your technical advantages and unique capabilities.',
      'product-roadmap': 'Upload your product roadmap showing 12-18 month vision.',
    },
    market: {
      'market-size': 'Include third-party market research to validate TAM/SAM/SOM.',
      'market-growth': 'Cite industry reports showing market growth trends.',
      'market-dynamics': 'Define your ideal customer profile more clearly.',
      'market-positioning': 'Sharpen your positioning against competitors.',
    },
    traction: {
      'traction-revenue': 'Focus on increasing MRR through customer expansion or new acquisition.',
      'traction-users': 'Accelerate user acquisition through targeted marketing.',
      'traction-engagement': 'Implement engagement metrics tracking and improve retention.',
      'traction-pmf': 'Conduct customer interviews to validate product-market fit signals.',
    },
    defensibility: {
      'defensibility-ip': 'Consider filing provisional patents for key innovations.',
      'defensibility-network': 'Build features that create data or network advantages.',
      'defensibility-switching': 'Deepen integrations to increase customer stickiness.',
      'defensibility-brand': 'Invest in thought leadership and brand building.',
    },
    competition: {
      'competition-landscape': 'Document your competitive analysis with detailed positioning.',
      'competition-differentiation': 'Quantify your advantages vs competitors (10x better on X).',
      'competition-response': 'Explain why incumbents cannot easily replicate your approach.',
    },
    'business-model': {
      'bm-revenue-model': 'Test and validate your pricing strategy with more customers.',
      'bm-unit-economics': 'Calculate and document LTV:CAC ratio and payback period.',
      'bm-profitability': 'Create financial projections showing path to profitability.',
      'bm-scalability': 'Document how costs scale (or don\'t) with growth.',
    },
    timing: {
      'timing-why-now': 'Articulate the specific catalysts enabling your opportunity.',
      'timing-tailwinds': 'Reference macro trends and industry shifts supporting growth.',
    },
    'vision-story': {
      'vision-clarity': 'Refine your vision statement to be more transformative.',
      'vision-storytelling': 'Work with a pitch coach to strengthen your narrative.',
    },
    'gtm-execution': {
      'gtm-strategy': 'Document your go-to-market strategy with specific channels.',
      'gtm-sales': 'Build a repeatable sales process and document it.',
      'gtm-marketing': 'Invest in marketing efforts and track ROI.',
    },
    'financial-health': {
      'fin-runway': 'Optimize burn rate or increase raise target for longer runway.',
      'fin-capital-efficiency': 'Focus on increasing revenue per dollar raised.',
      'fin-hygiene': 'Set up proper financial reporting and controls.',
    },
    'execution-velocity': {
      'exec-speed': 'Increase shipping velocity and document releases.',
      'exec-milestones': 'Set clear milestones and track achievement publicly.',
    },
    scalability: {
      'scale-tech': 'Document your architecture and conduct load testing.',
      'scale-ops': 'Automate manual processes to improve operational efficiency.',
      'scale-team': 'Build hiring pipeline and document culture practices.',
    },
    'deal-attractiveness': {
      'deal-terms-factor': 'Benchmark your deal terms against comparable stage companies.',
      'deal-exit-comps': 'Research and document exit comparables in your sector.',
    },
    'social-proof': {
      'social-investor-quality': 'Seek backing from recognized investors or angels.',
      'social-accelerator-tier': 'Apply to top accelerator programs in your domain.',
      'social-competition-wins': 'Enter startup competitions to build credibility.',
      'social-strategic-endorsements': 'Pursue strategic partnerships for external validation.',
    },
  };

  const dimensionRecs = recommendations[ds.dimension];
  if (dimensionRecs && dimensionRecs[lowestSub.criterionId]) {
    return dimensionRecs[lowestSub.criterionId];
  }

  // Generic fallback
  if (ds.rawScore < 40) {
    return `Critical improvement needed in ${ds.dimension}. Focus on addressing fundamental gaps.`;
  } else if (ds.rawScore < 60) {
    return `Strengthen ${ds.dimension} by uploading supporting documentation.`;
  } else if (ds.rawScore < 80) {
    return `Good ${ds.dimension} foundation. Consider verification to improve confidence.`;
  }

  return `Maintain strong ${ds.dimension} performance.`;
}

/**
 * Generate verification note based on evidence level
 */
function getVerificationNote(evidenceLevel: EvidenceLevel): string {
  switch (evidenceLevel) {
    case 'verified':
      return 'Data verified through third-party sources or API';
    case 'uploaded':
      return 'Supporting documents uploaded to data room';
    case 'in-progress':
      return 'Documentation being prepared';
    case 'self-declared':
      return 'Based on self-reported information';
    case 'missing':
      return 'No supporting data provided';
    default:
      return 'Verification status unknown';
  }
}

/**
 * Convenience function: Calculate ScoreCriteria[] directly from profile
 * This is the main entry point for the scoring system
 */
export function calculateScoreCriteria(
  profile: StartupProfile,
  options: CalculatorOptions = {}
): ScoreCriteria[] {
  const startupScore = calculateStartupScore(profile, options);
  return toScoreCriteria(startupScore);
}

/**
 * Calculate overall readiness score (0-100) from profile
 */
export function calculateReadinessScore(
  profile: StartupProfile,
  options: CalculatorOptions = {}
): number {
  const startupScore = calculateStartupScore(profile, options);
  return startupScore.adjustedScore;
}
